import { tracker } from "./mixpanel";

type ScrollDirection = "up" | "down";
type ScrollEventType = "start" | "end";

interface ScrollTrackerOptions {
    name: string;
    element?: HTMLElement | null;
}

export function trackScrollDepth({ name, element }: ScrollTrackerOptions): () => void {
    let lastScrollTop = 0;
    let isScrolling = false;
    let lastTrackedPercent: number | null = null;
    let isProcessing = false;

    const getScrollMetrics = (target: HTMLElement | Window): { scrollTop: number; scrollHeight: number } => {
        if (target instanceof Window) {
            return {
                scrollTop: target.scrollY,
                scrollHeight: document.documentElement.scrollHeight - target.innerHeight,
            };
        }
        return {
            scrollTop: target.scrollTop,
            scrollHeight: target.scrollHeight - target.clientHeight,
        };
    };

    const track = (
        percent: number,
        direction: ScrollDirection,
        eventType: ScrollEventType,
    ): void => {
        if (percent !== lastTrackedPercent && !isProcessing) {
            isProcessing = true;
            try {
                tracker.scroll.scrollStart(name, `${percent}%`, direction);
                lastTrackedPercent = percent;
            } catch (error) {
                console.log("Error in tracking scroll")
            } finally {
                isProcessing = false;
            }
        }
    };

    const handleScroll = (): void => {
        const { scrollTop, scrollHeight } = getScrollMetrics(scrollElement);

        if (scrollHeight <= 0) {
            return;
        }

        if (scrollTop === lastScrollTop) {
            return; // Skip if no movement
        }

        const percentScrolled = Math.min(100, Math.floor((scrollTop / scrollHeight) * 100));
        const direction: ScrollDirection = scrollTop > lastScrollTop ? "down" : "up";

        if (!isScrolling) {
            isScrolling = true;
            track(percentScrolled, direction, "start");
        }

        lastScrollTop = scrollTop;
    };

    const handleScrollEnd = (): void => {
        if (isScrolling) {
            const { scrollTop, scrollHeight } = getScrollMetrics(scrollElement);

            if (scrollHeight <= 0) {
                return;
            }

            if (scrollTop === lastScrollTop) {
                isScrolling = false;
                return; // Skip if no movement
            }

            const percentScrolled = Math.min(100, Math.floor((scrollTop / scrollHeight) * 100));
            const direction: ScrollDirection = scrollTop > lastScrollTop ? "down" : "up";

            track(percentScrolled, direction, "end");
            isScrolling = false;
        }
    };

    const scrollElement: HTMLElement | Window = element || window;
    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    scrollElement.addEventListener("scrollend", handleScrollEnd, { passive: true });


    return (): void => {
        scrollElement.removeEventListener("scroll", handleScroll);
        scrollElement.removeEventListener("scrollend", handleScrollEnd);
    };
}